import { notNullish } from '@getstep/sdk/dist/util/Assert'
import type { SdkClientStore } from '@lib/store/SdkClientStore'
import pickBy from 'lodash/pickBy'
import type { ParsedUrlQuery } from 'querystring'

import type { AppLinker } from './AppLinker'
import type { AppLinkData, AppLinkParameters } from './parameters'

/**
 * AppsFlyerLinker - Mobile Attribution & Deep Linking via AppsFlyer
 *
 * This class handles mobile attribution tracking and app download link generation
 * using AppsFlyer's OneLink technology. It serves two main purposes:
 *
 * 1. **Attribution Parameter Extraction**: Reads marketing parameters from URLs
 *    (UTM parameters, AppsFlyer-specific parameters) and formats them for tracking
 *
 * 2. **Smart App Link Generation**: Creates personalized download links that:
 *    - Direct users to the appropriate app store (iOS App Store / Google Play)
 *    - Preserve attribution data for when the user opens the app
 *    - Include custom data like referral codes, user info, etc.
 *
 * 3. **SMS Link Delivery**: Sends download links via SMS using Step's backend API,
 *    including attribution data and user identification for proper tracking
 */
export class AppsFlyerLinker implements AppLinker {
    name = 'AppsFlyer'

    constructor(private sdk: SdkClientStore) {}

    /**
     * Extracts AppsFlyer attribution parameters from URL query string
     *
     * This method parses marketing campaign parameters from the URL and formats them
     * for use in attribution tracking and app link generation.
     *
     * @param query - URL query parameters from Next.js router
     * @returns Formatted attribution parameters, or empty object if no AppsFlyer data
     *
     * Key parameters extracted:
     * - pid: Partner ID (indicates this is an AppsFlyer link)
     * - utm_source, utm_campaign, utm_medium: Standard marketing parameters
     * - af_* parameters: AppsFlyer-specific attribution data
     * - Custom data: Referral codes, user names, admin info, etc.
     */
    static appsFlyerParams(query: ParsedUrlQuery): Partial<AppLinkParameters> {
        // Process links that contain AppsFlyer or UTM attribution parameters
        // This captures users from AppsFlyer-tracked links (pid) OR standard UTM-tagged campaigns
        if (
            notNullish(query['pid']) ||
            notNullish(query['utm_source']) ||
            notNullish(query['utm_campaign'])
        ) {
            const params: Partial<AppLinkParameters> = {}

            // Extract standard AppsFlyer parameters (mediaSource, campaign, etc.)
            Object.entries(afParameters).forEach(
                ([key, { keys, defaultValue }]) => {
                    const value = keys.find((k) => query[k])
                    // @ts-ignore: type of key is string, which does not overlap with params keys
                    params[key] = notNullish(value)
                        ? query[value]
                        : defaultValue
                },
            )

            // Extract custom parameters (referral codes, user info, etc.)
            const data: AppLinkData = {}
            afCustom.forEach(({ paramKey, keys, defaultValue }) => {
                const value = keys?.find((k) => query[k])
                // @ts-ignore: type of paramKey is string, which does not overlap with AppLinkData keys
                data[paramKey] = notNullish(value) ? query[value] : defaultValue
            })

            // Remove null/undefined values to keep the data clean
            params.data = pickBy(data, (value) => value != null)
            return pickBy(params, (value) => value != null)
        }
        return {}
    }

    /**
     * Initialize the AppsFlyer linker
     *
     * No initialization needed since AppsFlyer Smart Script is loaded via GTM,
     * not dynamically imported.
     */
    async init(): Promise<AppLinkParameters | void> {
        // do nothing, AF_SMART_SCRIPT is loaded via Google Tag Manager
    }

    /**
     * Generates a personalized AppsFlyer OneLink URL for app downloads
     *
     * This method creates a smart download link that:
     * - Directs users to the correct app store based on their device
     * - Preserves all attribution data (campaign, source, custom parameters)
     * - Includes personalized data like referral codes and user information
     *
     * @param params - Attribution and custom data to include in the link
     * @returns Promise resolving to the generated OneLink URL or fallback organic link
     */
    link(params: AppLinkParameters): Promise<string | void> {
        const { data, ...trackingParams } = params

        // Check if Smart Script is loaded (via GTM)
        if (!window.AF_SMART_SCRIPT?.generateOneLinkURL) {
            console.debug(
                'AppsFlyer Smart Script not yet loaded, using organic URL fallback',
            )
            return Promise.resolve(
                process.env.NEXT_PUBLIC_APPSFLYER_ORGANIC_DOWNLOAD_URL,
            )
        }

        // Merge standard AppsFlyer parameters with provided tracking parameters
        const mergedAfParams = Object.fromEntries(
            Object.entries(afParameters).map(([key, value]) => [
                key,
                {
                    ...value,
                    defaultValue:
                        (trackingParams as any)[key] ?? value.defaultValue,
                },
            ]),
        )

        // Convert custom data fields to AppsFlyer parameter format
        const customAfParams = Object.keys(data).map((key) => ({
            paramKey: key,
            keys: [key],
            defaultValue: (data as any)[key],
        }))

        // Generate the OneLink URL using AppsFlyer's Smart Script (loaded via GTM)
        const link = window.AF_SMART_SCRIPT.generateOneLinkURL({
            oneLinkURL: process.env.NEXT_PUBLIC_APPSFLYER_ONELINK_URL!,
            afParameters: {
                ...mergedAfParams,
                afCustom: [
                    ...afCustom, // Predefined custom parameters
                    ...customAfParams, // Dynamic custom parameters from data
                    {
                        // Ensure referral code is always included if present
                        paramKey: 'code',
                        overrideValues: {},
                        keys: ['code'],
                        defaultValue: params.data.code,
                    },
                ],
            },
        })

        // Return the generated link or fallback to organic download URL
        return Promise.resolve(
            link?.clickURL ??
                process.env.NEXT_PUBLIC_APPSFLYER_ORGANIC_DOWNLOAD_URL,
        )
    }

    /**
     * Generates a QR code for the OneLink URL
     *
     * This method creates a QR code in a specified DOM element. The QR code will
     * contain the OneLink URL with attribution parameters, allowing users to scan
     * and download the app with proper attribution tracking.
     *
     * Prerequisites:
     * - Must call link() method first to generate the OneLink URL
     * - window.AF_SMART_SCRIPT must be loaded (via GTM)
     * - Target DOM element must exist
     *
     * @param elementId - ID of the DOM element where QR code will be rendered
     * @param options - Optional customization for QR code appearance
     * @returns The QR code instance or null if generation fails
     *
     */
    generateQrCode(
        elementId: string,
        options?: {
            logo?: string
            codeColor?: string
        },
    ): any {
        if (!window.AF_SMART_SCRIPT?.displayQrCode) {
            console.warn(
                'AppsFlyer Smart Script not loaded or displayQrCode not available',
            )
            return null
        }

        try {
            return window.AF_SMART_SCRIPT.displayQrCode(elementId, options)
        } catch (error) {
            console.error('Failed to generate QR code:', error)
            return null
        }
    }

    /**
     * Sends an app download link via SMS using Step's backend API
     *
     * This method delivers personalized download links to users via SMS, preserving
     * all attribution data and user identification for proper tracking when they
     * install and open the app.
     *
     * Attribution flow:
     * 1. User enters phone number on website
     * 2. SMS sent with AppsFlyer OneLink containing attribution data
     * 3. User clicks link → directed to app store with attribution preserved
     * 4. User installs app → AppsFlyer matches install to original campaign
     * 5. User opens app → attribution data available for onboarding/conversion tracking
     *
     * @param phoneNumber - User's phone number for SMS delivery
     * @param params - Attribution parameters and custom data to include
     * @returns Promise that resolves when SMS is sent (or undefined if phone invalid)
     */
    async sendSMS(
        phoneNumber: string,
        params: AppLinkParameters,
    ): Promise<string | void> {
        // Dynamically import phone utilities to reduce bundle size
        const { parsePhoneNumber } =
            await import('@getstep/sdk/dist/util/Phones')

        const api = await this.sdk.createPublicApi()
        const phone = parsePhoneNumber(phoneNumber)

        // Extract attribution and campaign data
        const { channel, campaign, mediaSource, data, templateId } = params

        // Separate AppsFlyer-specific parameters from other custom data
        const {
            af_c_id, // AppsFlyer Campaign ID
            af_siteid, // AppsFlyer Site ID
            af_sub_siteid, // AppsFlyer Sub-Site ID
            clickid, // Click identifier for attribution
            af_click_lookback, // Attribution window
            expedited_onboarding, // Special onboarding flag
            ...otherData // All other custom parameters
        } = data

        // Filter out null/undefined values for cleaner data
        const metadata = Object.fromEntries(
            Object.entries(otherData).filter(
                ([, value]) => value !== null && value !== undefined,
            ),
        )

        // Send SMS only if phone number is valid
        if (phone.valid) {
            await api.sendDownloadSms({
                phone: phone.digits,
                templateId, // SMS template to use
                channel,
                campaign,
                campaignId: af_c_id,
                partnerId: mediaSource ?? '',
                siteId: af_siteid,
                subSiteId: af_sub_siteid,
                clickId: clickid,
                clickLookback: af_click_lookback,
                stepConversionData: {
                    code: data.code, // Referral/invite code
                    segmentUserId: data.segment_user_id, // User ID for attribution
                },
                metadata, // Additional custom parameters
            })
        }
        return undefined
    }
}

/**
 * AppsFlyer Parameter Configuration
 *
 * These objects define how URL parameters are mapped to AppsFlyer attribution data:
 * - afParameters: Standard AppsFlyer parameters (defined by AppsFlyer)
 * - afCustom: Custom parameters defined by Step for specific tracking needs
 *
 * Reference: https://support.appsflyer.com/hc/en-us/articles/4413588932241#configuration-object
 */

/**
 * Standard AppsFlyer attribution parameters
 *
 * Maps various URL parameter names to standardized AppsFlyer fields.
 * Supports multiple parameter names for the same field (e.g., 'source', 'utm_source', 'pid' all map to mediaSource).
 */
const afParameters: Record<
    string,
    {
        keys: string[] // Alternative parameter names to check for
        overrideValues: Record<string, any> // Value overrides for specific cases
        defaultValue?: string // Default value if parameter not found
    }
> = {
    mediaSource: {
        keys: ['source', 'utm_source', 'pid'],
        overrideValues: {},
        defaultValue: 'web_organic',
    },
    campaign: {
        keys: ['c', 'campaign', 'utm_campaign'],
        overrideValues: {},
        defaultValue: 'Website',
    },
    ad: {
        keys: ['af_ad', 'utm_ad', 'utm_content'],
        overrideValues: {},
    },
    adSet: {
        keys: ['af_adset', 'adset'],
        overrideValues: {},
    },
    channel: {
        keys: ['af_channel', 'channel', 'utm_channel'],
        overrideValues: {},
        defaultValue: 'Website',
    },
    feature: {
        keys: ['utm_medium'],
        overrideValues: {},
    },
    deepLinkValue: {
        keys: ['deep_link_value'],
        overrideValues: {},
    },
}

/**
 * Custom Step-specific parameters for AppsFlyer links
 *
 * These parameters carry Step-specific data through the attribution flow:
 * - User information: Names of sponsors, referrers, admins, invitees
 * - AppsFlyer technical data: Campaign IDs, site IDs, click tracking data
 * - Special flags: UI preferences, attribution windows
 *
 * This data is preserved through the app download → install → first open flow,
 * enabling personalized onboarding experiences and proper attribution tracking.
 */
const afCustom = [
    {
        paramKey: 'sponsee_full_name',
        keys: ['sponsee_full_name'],
    },
    {
        paramKey: 'sponsee_first_name',
        keys: ['sponsee_first_name'],
    },
    {
        paramKey: 'sponsee_last_name',
        keys: ['sponsee_last_name'],
    },
    {
        paramKey: 'sponsor_full_name',
        keys: ['sponsor_full_name'],
    },
    {
        paramKey: 'sponsor_first_name',
        keys: ['sponsor_first_name'],
    },
    {
        paramKey: 'sponsor_last_name',
        keys: ['sponsor_last_name'],
    },
    {
        paramKey: 'admin_full_name',
        keys: ['admin_full_name'],
    },
    {
        paramKey: 'admin_first_name',
        keys: ['admin_first_name'],
    },
    {
        paramKey: 'admin_last_name',
        keys: ['admin_last_name'],
    },
    {
        paramKey: 'inviter_first_name',
        keys: ['inviter_first_name'],
    },
    {
        paramKey: 'inviter_last_name',
        keys: ['inviter_last_name'],
    },
    {
        paramKey: 'inviter_full_name',
        keys: ['inviter_full_name'],
    },
    {
        paramKey: 'af_c_id',
        keys: ['af_c_id'],
    },
    {
        paramKey: 'af_siteid',
        keys: ['af_siteid'],
    },
    {
        paramKey: 'af_sub_siteid',
        keys: ['af_sub_siteid'],
    },
    {
        paramKey: 'clickid',
        keys: ['clickid'],
    },
    {
        paramKey: 'af_click_lookback',
        keys: ['af_click_lookback'],
    },
    {
        paramKey: 'af_ss_ui',
        defaultValue: 'true',
    },
    {
        paramKey: 'af_ss_gtm_ui',
        defaultValue: 'true',
    },
]
